import { cloneDeep } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import { SortByMode } from '~/src/dash/components/material-search-dropdown-multi-list';
import { Div, TKey, usePublicaTranslation } from '~/src/dash/publicaUI';
import { IMenuList } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';

const getSelectedFilters = (list: IMenuList) => {
  const result = {} as IMenuList;
  Object.values(list).forEach((item) => {
    if (item.selected) {
      result[item.ID] = item;
    }
  });
  return result;
};

interface IFilterPillProps {
  menuTitle: string;
  // menuTitle is plural ex: 'Countries', singularTitle ex: 'Country'
  singularTitle?: string;
  list: IMenuList;
  activateFilterClear: Record<string, boolean>;
  handleFilterAccept: (menuTitle: string, list: IMenuList) => void;
  sortMode?: SortByMode;
  tKey?: TKey;
}

export const FilterPill = ({
  menuTitle,
  singularTitle,
  list,
  activateFilterClear,
  handleFilterAccept,
  sortMode,
  tKey,
}: IFilterPillProps) => {
  // declare states and refs
  const isMounted = useRef(false);
  const { t } = usePublicaTranslation();
  const [storedList, setStoredList] = useState(list);
  const [hasListChanged, setHasListChanged] = useState(false);

  const numSelected = Object.values(storedList).filter((item) => item.selected).length;

  // instead of iterating through the list and changing selected to false,
  // we instead default it to the original list on clear
  useEffect(() => {
    if (isMounted.current && activateFilterClear.clear) {
      const clonedList = cloneDeep(list);
      unstable_batchedUpdates(() => {
        setStoredList(clonedList);
      });
    }
  }, [activateFilterClear]);

  useEffect(() => {
    if (isMounted.current) {
      setHasListChanged(true);
    } else {
      isMounted.current = true;
    }
  }, [storedList]);

  const handleOnChange = (updatedList: IMenuList) => {
    setStoredList(updatedList);
  };

  const handleAccept = () => {
    if (hasListChanged) {
      handleFilterAccept(menuTitle, getSelectedFilters(storedList));
      setHasListChanged(false);
    }
  };

  return (
    <Div
      style={{
        border: `1px solid ${publicaColors.lightGray}`,
        backgroundColor: numSelected ? publicaColors.honeydew : publicaColors.veryLightGray,
        borderRadius: '6px',
        padding: '2px 4px 2px 12px',
        margin: '2px 8px 2px 0px',
        cursor: 'pointer',
      }}
    >
      <MaterialSearchDropdownMulti
        disabled={false}
        fontSize='12px'
        isFontBold
        fontColor={numSelected ? publicaColors.jungleGreen : publicaColors.black}
        menuTitle={tKey ? t(tKey) : menuTitle}
        singularTitle={singularTitle}
        list={storedList}
        onChange={handleOnChange}
        handleMenuAcceptClick={handleAccept}
        onlyShowNumberWhenClosed={true}
        maxHeight={320}
        selectAll={false}
        disableBottomBorder={true}
        overrideDefaultSortMode={sortMode}
        responsivePositionEnabled
      />
    </Div>
  );
};
